import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { db } from "../firebase";

function EmployeeReports() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const reportType =
    searchParams.get("type") || "leads";

  const [employee, setEmployee] =
    useState(null);

  const [leads, setLeads] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [searchText, setSearchText] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("All");

  const [fromDate, setFromDate] =
    useState("");

  const [toDate, setToDate] =
    useState("");

  const today =
    new Date()
      .toISOString()
      .split("T")[0];

  const reportTitles = {
    leads: "Total Leads",
    calls: "Calls Done Today",
    pending: "Pending Leads",
    "today-callbacks": "Today’s Callbacks",
    callbacks: "Pending Callbacks",
    missed: "Missed Callbacks",
    interested: "Interested Leads",
    converted: "Converted Leads",
  };

  useEffect(() => {
    const savedEmployee =
      localStorage.getItem(
        "crmEmployee"
      );

    if (!savedEmployee) {
      navigate(
        "/employee-login"
      );
      return;
    }

    let parsedEmployee;

    try {
      parsedEmployee =
        JSON.parse(
          savedEmployee
        );
    } catch (error) {
      localStorage.removeItem(
        "crmEmployee"
      );

      navigate(
        "/employee-login"
      );
      return;
    }

    if (
      !parsedEmployee?.email
    ) {
      navigate(
        "/employee-login"
      );
      return;
    }

    setEmployee(
      parsedEmployee
    );

    const loadLeads =
      async () => {
        try {
          setLoading(true);

          const employeeEmail =
            parsedEmployee.email
              .trim()
              .toLowerCase();

          const leadQuery =
            query(
              collection(
                db,
                "leads"
              ),
              where(
                "assignedEmployeeEmail",
                "==",
                employeeEmail
              )
            );

          const snapshot =
            await getDocs(
              leadQuery
            );

          setLeads(
            snapshot.docs.map(
              (item) => ({
                id: item.id,
                ...item.data(),
              })
            )
          );
        } catch (error) {
          console.error(
            "Employee report load error:",
            error
          );
        } finally {
          setLoading(false);
        }
      };

    loadLeads();
  }, [navigate]);

  const toDateObject = (
    value
  ) => {
    if (!value) {
      return null;
    }

    try {
      if (
        typeof value.toDate ===
        "function"
      ) {
        return value.toDate();
      }

      const date =
        new Date(value);

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return null;
      }

      return date;
    } catch (error) {
      return null;
    }
  };

  const getTimeValue = (
    value
  ) => {
    const date =
      toDateObject(value);

    return date
      ? date.getTime()
      : 0;
  };

  const getDateKey = (
    value
  ) => {
    const date =
      toDateObject(value);

    if (!date) {
      return "";
    }

    return date
      .toISOString()
      .split("T")[0];
  };

  const formatDate = (
    value
  ) => {
    const date =
      toDateObject(value);

    if (!date) {
      return "-";
    }

    return date.toLocaleString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const withinDateFilter = (
    value
  ) => {
    if (
      !fromDate &&
      !toDate
    ) {
      return true;
    }

    const key =
      typeof value ===
        "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(
        value
      )
        ? value
        : getDateKey(
            value
          );

    if (!key) {
      return false;
    }

    if (
      fromDate &&
      key < fromDate
    ) {
      return false;
    }

    if (
      toDate &&
      key > toDate
    ) {
      return false;
    }

    return true;
  };

  const hasCallToday = (
    lead
  ) => {
    const history =
      Array.isArray(
        lead.remarksHistory
      )
        ? lead.remarksHistory
        : [];

    return history.some(
      (item) =>
        getDateKey(
          item.createdAt
        ) === today
    );
  };

  const reportRows =
    useMemo(() => {
      let list =
        [...leads];

      if (
        reportType ===
        "calls"
      ) {
        list =
          list.filter(
            hasCallToday
          );
      }

      if (
        reportType ===
        "pending"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.status !==
                "Converted" &&
              lead.status !==
                "Prospect" &&
              lead.status !==
                "Not Interested"
          );
      }

      if (
        reportType ===
        "today-callbacks"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.callbackStatus ===
                "Pending" &&
              lead.callbackDate ===
                today
          );
      }

      if (
        reportType ===
        "callbacks"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.callbackStatus ===
              "Pending"
          );
      }

      if (
        reportType ===
        "missed"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.callbackStatus ===
                "Pending" &&
              lead.callbackDate &&
              lead.callbackDate <
                today
          );
      }

      if (
        reportType ===
        "interested"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.status ===
                "Interested" ||
              lead.lastDisposition ===
                "Interested"
          );
      }

      if (
        reportType ===
        "converted"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.status ===
              "Converted"
          );
      }

      if (
        statusFilter !==
        "All"
      ) {
        list =
          list.filter(
            (lead) =>
              (
                lead.status ||
                "Pending"
              ) ===
              statusFilter
          );
      }

      if (
        fromDate ||
        toDate
      ) {
        list =
          list.filter(
            (lead) => {
              if (
                reportType.includes(
                  "callback"
                ) ||
                reportType ===
                  "missed"
              ) {
                return withinDateFilter(
                  lead.callbackDate
                );
              }

              return withinDateFilter(
                lead.lastContactedAt ||
                  lead.createdAt
              );
            }
          );
      }

      const search =
        searchText
          .trim()
          .toLowerCase();

      if (search) {
        list =
          list.filter(
            (lead) =>
              (
                lead.customerName ||
                lead.name ||
                ""
              )
                .toLowerCase()
                .includes(
                  search
                ) ||
              String(
                lead.phone ||
                  ""
              ).includes(
                search
              ) ||
              (
                lead.email ||
                ""
              )
                .toLowerCase()
                .includes(
                  search
                ) ||
              (
                lead.lastRemark ||
                ""
              )
                .toLowerCase()
                .includes(
                  search
                )
          );
      }

      /*
        Sorting rule for every employee report:

        1. Fresh / untouched leads on top
        2. Worked leads below
        3. Among worked leads, the most recently
           updated lead goes furthest down
      */

      list.sort(
        (a, b) => {
          const aWorked =
            getTimeValue(
              a.lastContactedAt
            );

          const bWorked =
            getTimeValue(
              b.lastContactedAt
            );

          if (
            !aWorked &&
            bWorked
          ) {
            return -1;
          }

          if (
            aWorked &&
            !bWorked
          ) {
            return 1;
          }

          /*
            Both are fresh:
            newest fresh lead first.
          */

          if (
            !aWorked &&
            !bWorked
          ) {
            return (
              getTimeValue(
                b.createdAt
              ) -
              getTimeValue(
                a.createdAt
              )
            );
          }

          /*
            Both worked:
            older worked record first,
            most recently updated record last.
          */

          return (
            aWorked -
            bWorked
          );
        }
      );

      return list;
    }, [
      leads,
      reportType,
      statusFilter,
      searchText,
      fromDate,
      toDate,
      today,
    ]);

  return (
    <div className="employee-calls-page">

      <header className="mobile-crm-header">

        <div>

          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Employee Report
          </p>

        </div>

        <button
          type="button"
          className="mobile-back-button"
          onClick={() =>
            navigate(
              "/employee-dashboard"
            )
          }
        >
          Dashboard
        </button>

      </header>

      <main className="employee-calls-main">

        <section className="employee-calls-heading">

          <p className="small-label">
            EMPLOYEE REPORT
          </p>

          <h2>
            {reportTitles[
              reportType
            ] ||
              "Customer Records"}
          </h2>

          <p>
            {employee?.name ||
              "Employee"}{" "}
            – view and filter your assigned customers.
          </p>

        </section>

        <section className="employee-update-card">

          <div className="callback-fields-grid">

            <div className="form-group">

              <label>
                Search Customer
              </label>

              <input
                type="text"
                value={
                  searchText
                }
                onChange={(
                  event
                ) =>
                  setSearchText(
                    event.target.value
                  )
                }
                placeholder="Name, phone, email or remark"
              />

            </div>

            <div className="form-group">

              <label>
                Status
              </label>

              <select
                value={
                  statusFilter
                }
                onChange={(
                  event
                ) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >

                <option value="All">
                  All Statuses
                </option>

                <option value="Pending">
                  Pending
                </option>

                <option value="Interested">
                  Interested
                </option>

                <option value="Not Interested">
                  Not Interested
                </option>

                <option value="Busy">
                  Busy
                </option>

                <option value="Ringing">
                  Ringing
                </option>

                <option value="Not Answered">
                  Not Answered
                </option>

                <option value="Prospect">
                  Prospect
                </option>

                <option value="Converted">
                  Converted
                </option>

              </select>

            </div>

            <div className="form-group">

              <label>
                From Date
              </label>

              <input
                type="date"
                value={
                  fromDate
                }
                onChange={(
                  event
                ) =>
                  setFromDate(
                    event.target.value
                  )
                }
              />

            </div>

            <div className="form-group">

              <label>
                To Date
              </label>

              <input
                type="date"
                value={
                  toDate
                }
                onChange={(
                  event
                ) =>
                  setToDate(
                    event.target.value
                  )
                }
              />

            </div>

          </div>

          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setSearchText("");

              setStatusFilter(
                "All"
              );

              setFromDate("");

              setToDate("");
            }}
          >
            Clear Filters
          </button>

        </section>

        <section className="dashboard-section">

          <div className="section-heading">

            <div>

              <p className="small-label">
                CUSTOMER RECORDS
              </p>

              <h2>
                {
                  reportRows.length
                }{" "}
                Records
              </h2>

            </div>

          </div>

          {loading ? (

            <div className="employee-empty-state">
              Loading...
            </div>

          ) : reportRows.length ===
            0 ? (

            <div className="employee-empty-state">
              No records found.
            </div>

          ) : (

            <div className="mobile-lead-list">

              {reportRows.map(
                (lead) => (

                  <article
                    key={
                      lead.id
                    }
                    className="mobile-lead-card"
                  >

                    <div className="mobile-lead-card-top">

                      <div>

                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/employee-calls?lead=${lead.id}`
                            )
                          }
                          style={{
                            border:
                              "none",

                            background:
                              "transparent",

                            padding: 0,

                            fontSize:
                              "17px",

                            fontWeight:
                              "800",

                            cursor:
                              "pointer",

                            color:
                              "#0284c7",
                          }}
                        >
                          {lead.customerName ||
                            lead.name ||
                            "Customer"}
                        </button>

                        <p>
                          {lead.phone ||
                            "-"}
                        </p>

                      </div>

                      <span className="mobile-status pending">
                        {lead.status ||
                          "Pending"}
                      </span>

                    </div>

                    {lead.callbackDate && (

                      <div className="mobile-lead-meta">

                        <span>
                          Callback:{" "}
                          {
                            lead.callbackDate
                          }{" "}
                          {
                            lead.callbackTime
                          }
                        </span>

                      </div>

                    )}

                    {lead.lastRemark && (

                      <p className="latest-remark-preview">
                        Latest:{" "}
                        {
                          lead.lastRemark
                        }
                      </p>

                    )}

                    <div className="mobile-lead-meta">

                      <span>
                        Last Contact:{" "}
                        {formatDate(
                          lead.lastContactedAt
                        )}
                      </span>

                    </div>

                    <div className="mobile-lead-actions">

                      <a
                        className="small-call-button"
                        href={`tel:${
                          lead.phone ||
                          ""
                        }`}
                      >
                        Call
                      </a>

                      <button
                        type="button"
                        className="open-lead-button"
                        onClick={() =>
                          navigate(
                            `/employee-calls?lead=${lead.id}`
                          )
                        }
                      >
                        Open & Update
                      </button>

                    </div>

                  </article>

                )
              )}

            </div>

          )}

        </section>

      </main>

    </div>
  );
}

export default EmployeeReports;